/**
 * School Information Reports API Service
 * Fetches school-level report data (enrollment, repeaters, dropout, teachers, etc.)
 */

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1') + '/sis-integration';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ReportType {
  key: string;
  title: string;
  category: string;
  description: string;
}

export interface ReportColumn {
  key: string;
  label: string;
  sub_columns?: string[];
  type?: string;
}

export interface SchoolRow {
  sn: number;
  school_name: string;
  school_level?: string;
  values: Record<string, Record<string, number> | number | string>;
  total_male?: number;
  total_female?: number;
  grand_total?: number;
}

export interface ReportSummary {
  total_schools: number;
  values?: Record<string, Record<string, number>>;
  total_male?: number;
  total_female?: number;
  grand_total?: number;
}

export interface ReportData {
  report_title: string;
  district_name: string;
  academic_year: number;
  columns: ReportColumn[];
  schools: SchoolRow[];
  summary?: ReportSummary;
  flat_report?: boolean;
}

export interface ReportResponse {
  status: string;
  data: ReportData;
  message?: string;
}

export interface DistrictOption {
  district_id: string;
  district_name: string;
}

// ─── API Functions ───────────────────────────────────────────────────────────

export const fetchReportTypes = async (): Promise<ReportType[]> => {
  const response = await fetch(`${API_BASE_URL}/school-reports/types/`);
  if (!response.ok) throw new Error('Failed to fetch report types');
  const data = await response.json();
  return data.data;
};

export const fetchReport = async (
  reportType: string,
  year: number,
  districtId?: string,
  schoolType?: string,
  educationLevel?: string,
): Promise<ReportResponse> => {
  const params = new URLSearchParams({
    report_type: reportType,
    year: String(year),
  });
  if (districtId) params.append('district_id', districtId);
  if (schoolType) params.append('school_type', schoolType);
  if (educationLevel) params.append('education_level', educationLevel);

  const response = await fetch(`${API_BASE_URL}/school-reports/?${params}`);
  if (!response.ok) throw new Error('Failed to fetch report');
  return response.json();
};

export const fetchReportDistricts = async (): Promise<DistrictOption[]> => {
  const response = await fetch(`${API_BASE_URL}/school-reports/districts/`);
  if (!response.ok) throw new Error('Failed to fetch districts');
  const data = await response.json();
  return data.data;
};
